<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <h3 class="text-xl font-semibold text-gray-800 mb-4">
      Worked Hours Summary
    </h3>

    <div v-if="loading" class="text-gray-500">Loading report...</div>

    <div v-else-if="!summary || summary.length === 0" class="text-gray-500">
      No data found for the selected period.
    </div>

    <div v-else class="space-y-4">
      <!-- Total acumulado -->
      <div class="p-4 bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
        <div class="flex justify-between items-center">
          <div>
            <h4 class="text-lg font-bold text-blue-800">Total Hours</h4>
            <p class="text-sm text-blue-600">All tasks combined</p>
          </div>
          <div class="text-right">
            <p class="text-2xl font-bold text-blue-800">{{ totalHours }}</p>
            <p class="text-sm text-blue-600">({{ totalHoursParts.hours }}h {{ totalHoursParts.minutes }}m)</p>
          </div>
        </div>
      </div>

      <!-- Lista de tareas -->
      <div
        v-for="task in summary.summary || summary"
        :key="task.id_task || task.task?.id"
        class="p-4 bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow"
      >
        <div class="flex justify-between items-start">
          <div class="flex-1">
            <div class="flex items-center gap-2 mb-2">
              <h4 class="text-lg font-bold text-gray-800">
                {{ task.task?.name || 'Unnamed Task' }}
              </h4>
              <span 
                class="px-2 py-1 text-xs rounded-full"
                :class="getStatusClass(task.task?.status)"
              >
                {{ task.task?.status || 'Unknown' }}
              </span>
              <span 
                class="px-2 py-1 text-xs rounded-full"
                :class="task.task?.billable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
              >
                {{ task.task?.billable ? "Billable" : "Non-Billable" }}
              </span>
            </div>
            
            <p class="text-sm text-gray-600 mb-2">
              {{ task.task?.description || 'No description available' }}
            </p>
            
            <div class="flex flex-wrap gap-4 text-sm text-gray-500">
              <p>
                <span class="font-semibold">Project:</span>
                {{ task.task?.id_project || 'N/A' }}
              </p>
              <p>
                <span class="font-semibold">Task ID:</span>
                {{ task.id_task || task.task?.id || 'N/A' }}
              </p>
            </div>
          </div>
          
          <div class="text-right ml-4">
            <div class="bg-gray-50 p-3 rounded-lg">
              <p class="text-lg font-bold text-gray-800">
                {{ task.total_hms || '0:00' }}
              </p>
              <p class="text-sm text-gray-600">
                ({{ (task.total_parts?.hours || 0) }}h {{ (task.total_parts?.minutes || 0) }}m)
              </p>
            </div>
          </div>
        </div>

        <!-- Barra de progreso visual (opcional) -->
        <div class="mt-3 pt-3 border-t">
          <div class="flex justify-between text-sm text-gray-700">
            <span class="font-semibold">Time Allocation:</span>
            <span>{{ calculatePercentage(task) }}% of total</span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2 mt-1">
            <div 
              class="bg-blue-600 h-2 rounded-full" 
              :style="{ width: calculatePercentage(task) + '%' }"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted, computed } from "vue";
import { useStore } from "vuex"

export default {
  name: 'WorkedHoursReport',
  props: {
    filters: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    console.log("🚀 ~ props:", props)
    const store = useStore()
    const summary = ref([]);
    const loading = ref(false);

    // Calcular total acumulado
    const totalHours = computed(() => {
      if (!summary.value || !summary.value.summary) return '0:00';
      
      const totalMinutes = summary.value.summary.reduce((acc, task) => {
        const parts = task.total_parts || { hours: 0, minutes: 0 };
        return acc + (parts.hours * 60) + parts.minutes;
      }, 0);
      
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    });
    
    // Desglose del total en horas y minutos
    const totalHoursParts = computed(() => {
      if (!summary.value || !summary.value.summary) return { hours: 0, minutes: 0 };
      
      const totalMinutes = summary.value.summary.reduce((acc, task) => {
        const parts = task.total_parts || { hours: 0, minutes: 0 };
        return acc + (parts.hours * 60) + parts.minutes;
      }, 0);
      
      return {
        hours: Math.floor(totalMinutes / 60),
        minutes: totalMinutes % 60
      };
    });

    // Clases para los estados
    const getStatusClass = (status) => {
      const statusMap = {
        'completed': 'bg-green-100 text-green-800',
        'in-progress': 'bg-blue-100 text-blue-800',
        'pending': 'bg-yellow-100 text-yellow-800',
        'cancelled': 'bg-red-100 text-red-800'
      };
      return statusMap[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
    };

    // Calcular porcentaje para la barra de progreso
    const calculatePercentage = (task) => {
      if (!summary.value || !summary.value.summary || totalHoursParts.value.hours === 0) return 0;
      
      const taskMinutes = ((task.total_parts?.hours || 0) * 60) + (task.total_parts?.minutes || 0);
      const totalMinutes = (totalHoursParts.value.hours * 60) + totalHoursParts.value.minutes;
      
      return Math.round((taskMinutes / totalMinutes) * 100);
    };

    const fetchWorkedHours = async () => {
      if (!props.filters?.startDate || !props.filters?.endDate) return;

      try {
        loading.value = true;
        const response = await store.dispatch("fetchWorkedHoursReport", {
          startDate: props.filters.startDate,
          endDate: props.filters.endDate,
          type: props.filters.workedHoursType,
          value: props.filters.selectedUser,
          userTimezone: props.filters.userTimezone
        })
        console.log("response..........", response)
        summary.value = response || [];
      } catch (error) {
        console.error("Error loading report:", error);
      } finally {
        loading.value = false;
      }
    };

    // Watcher para cambios en los filtros
    watch(
      () => props.filters,
      () => {
        fetchWorkedHours();
      },
      { deep: true, immediate: true }
    );

    // También puedes usar onMounted si necesitas ejecutar algo al montar el componente
    onMounted(() => {
      console.log('WorkedHoursReport component mounted');
    });

    return {
      summary,
      loading,
      totalHours,
      totalHoursParts,
      getStatusClass,
      calculatePercentage
    };
  }
};
</script>